import { JobTetrisMark } from '../../components/ui/JobTetrisMark';

/**
 * Screen 12 — Session error states.
 * Left card triggers on 401 token_expired.
 * Right card triggers after 3 consecutive 5xx errors.
 */
export function SessionErrorPage() {
  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100vw',
        backgroundColor: 'var(--color-surface-page)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* Minimal header */}
      <header
        style={{
          height: 'var(--topbar-height)',
          backgroundColor: 'var(--topbar-bg)',
          borderBottom: '1px solid var(--topbar-border)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 var(--space-6)',
          flexShrink: 0,
        }}
      >
        <HeaderLogos />
      </header>

      {/* Body */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--space-10) var(--space-6)',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--space-6)',
            width: '100%',
            maxWidth: '880px',
          }}
        >
          <ExpiredCard />
          <UnreachableCard />
        </div>
      </main>
    </div>
  );
}

/* ── Left card: session expired ── */

function ExpiredCard() {
  return (
    <article
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--card-padding)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 'var(--space-4)',
      }}
    >
      <IconBadge tone="warning">
        <IconClock size={22} />
      </IconBadge>

      <h2
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-text-brand)',
          letterSpacing: 'var(--tracking-tight)',
          lineHeight: 'var(--leading-tight)',
        }}
      >
        Your session has expired
      </h2>

      <p
        style={{
          fontSize: 'var(--text-md)',
          color: 'var(--color-text-secondary)',
          lineHeight: 'var(--leading-relaxed)',
        }}
      >
        For your security, Engage signs you out after 30 minutes of inactivity. Head back to JobTetris
        and re-launch Engage to pick up where you left off.
      </p>

      <PrimaryButton href="https://app.jobtetris.com">Return to JobTetris</PrimaryButton>

      <CardFooter>app.jobtetris.com</CardFooter>
    </article>
  );
}

/* ── Right card: connection lost ── */

function UnreachableCard() {
  return (
    <article
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--card-padding)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 'var(--space-4)',
      }}
    >
      <IconBadge tone="danger">
        <IconX size={22} />
      </IconBadge>

      <h2
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-text-brand)',
          letterSpacing: 'var(--tracking-tight)',
          lineHeight: 'var(--leading-tight)',
        }}
      >
        We can&rsquo;t reach JobTetris
      </h2>

      <p
        style={{
          fontSize: 'var(--text-md)',
          color: 'var(--color-text-secondary)',
          lineHeight: 'var(--leading-relaxed)',
        }}
      >
        Engage couldn&rsquo;t talk to the JobTetris API after several attempts. We&rsquo;re retrying
        automatically in the background. You can return to JobTetris or try again now.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
        <PrimaryButton href="https://app.jobtetris.com">Return to JobTetris</PrimaryButton>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.location.reload();
          }}
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-link)',
            textDecoration: 'none',
          }}
        >
          Retry connection
        </a>
      </div>

      <CardFooter>app.jobtetris.com</CardFooter>
    </article>
  );
}

/* ── Shared pieces (also used by NoAccessPage) ── */

export function HeaderLogos() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
      <JobTetrisMark size={24} decorative />
      <span
        style={{
          fontSize: 'var(--text-md)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-text-brand)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        JobTetris
      </span>

      <span
        aria-hidden="true"
        style={{
          width: '1px',
          height: '20px',
          backgroundColor: 'var(--color-border-default)',
          marginInline: 'var(--space-1)',
        }}
      />

      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: 'var(--radius-chip)',
          backgroundColor: 'var(--color-brand-blue)',
          flexShrink: 0,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M6 1L11 6L6 11M1 6H11"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span
        style={{
          fontSize: 'var(--text-md)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-text-brand)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        Engage
      </span>
    </div>
  );
}

export function IconBadge({ tone, children }: { tone: 'warning' | 'danger'; children: React.ReactNode }) {
  const bg = tone === 'warning' ? 'var(--color-warning-bg)' : 'var(--color-danger-bg)';
  const fg = tone === 'warning' ? 'var(--color-warning)' : 'var(--color-danger)';
  const border = tone === 'warning' ? 'var(--color-warning-border)' : 'var(--color-danger-border)';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '44px',
        height: '44px',
        borderRadius: 'var(--radius-full)',
        backgroundColor: bg,
        border: `1px solid ${border}`,
        color: fg,
      }}
    >
      {children}
    </span>
  );
}

export function PrimaryButton({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-primary)',
        color: 'var(--color-action-primary-text)',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        fontFamily: 'var(--font-sans)',
        textDecoration: 'none',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color var(--transition-base)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--color-action-primary-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--color-action-primary)';
      }}
    >
      {children}
    </a>
  );
}

export function CardFooter({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 'var(--space-2)',
        paddingTop: 'var(--space-3)',
        borderTop: '1px solid var(--color-border-default)',
        width: '100%',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-medium)',
        color: 'var(--color-text-subtle)',
        letterSpacing: 'var(--tracking-wide)',
        fontFamily: 'var(--font-mono)',
      }}
    >
      {children}
    </div>
  );
}

/* ── Icons (Feather) ── */

function IconClock({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function IconX({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
